import { type ReactNode, useRef, useEffect, useState } from 'react';
import { observeNewsletterLanding, trackNewsletterAction } from '@/lib/subscribeFunnelAnalytics';
import NewsletterHeroDemo from '../components/NewsletterHeroDemo';
import CustomerLogos from './CustomerLogos';
import NewsletterBenefitDialog, {
  type NewsletterBenefit,
} from '../components/NewsletterBenefitDialog';

export default function Hero({
  socialProof,
  headline,
}: {
  socialProof?: ReactNode;
  headline?: ReactNode;
}) {
  const [benefit, setBenefit] = useState<NewsletterBenefit | null>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!headlineRef.current) return;
    return observeNewsletterLanding(headlineRef.current);
  }, []);
  return (
    <div
      id="top"
      data-newsletter-section="intro"
      className="newsletter-home-hero grain relative isolate overflow-hidden bg-nl-paper px-4 pt-3 pb-8 sm:px-6 sm:pt-6 md:pt-5 md:pb-0 xl-wide:flex"
    >
      <div className="newsletter-home-hero-inner relative mx-auto flex w-full max-w-6xl flex-1 flex-col">
        <div id="marketing-nav-sentinel" className="h-px w-full" aria-hidden />

        <div
          data-testid="newsletter-hero-content-grid"
          className="newsletter-home-hero-content mt-3 grid items-center gap-3 sm:mt-6 sm:gap-6 xl-wide:mt-2 xl-wide:min-h-0 xl-wide:flex-1 xl-wide:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] xl-wide:gap-12"
        >
          <section className="min-w-0">
            <div className="eyebrow newsletter-hero-eyebrow mb-3">For Newsletter Operators</div>
            <h1
              ref={headlineRef}
              className="h-display max-w-4xl text-[clamp(1.75rem,9vw,3rem)] leading-[0.95] font-medium text-balance sm:leading-[0.9] lg:text-6xl"
            >
              {headline ?? (
                <>
                  <span className="sm:block">Give readers</span>{' '}
                  <span className="sm:block">another reason to open.</span>
                </>
              )}
            </h1>

            <p className="mt-5 max-w-2xl text-base leading-snug font-normal text-nl-ink/70 sm:mt-6 sm:text-lg sm:leading-relaxed md:text-xl">
              Turn your content into gamified quizzes to{' '}
              <button
                type="button"
                className="accent-underline newsletter-benefit-trigger font-bold text-nl-ink"
                aria-haspopup="dialog"
                onClick={() => {
                  trackNewsletterAction('hero_benefit_habit');
                  setBenefit('habit');
                }}
              >
                build a reader habit
              </button>{' '}
              and{' '}
              <button
                type="button"
                className="accent-underline newsletter-benefit-trigger font-bold text-nl-ink"
                aria-haspopup="dialog"
                onClick={() => {
                  trackNewsletterAction('hero_benefit_sponsorship');
                  setBenefit('sponsorship');
                }}
              >
                earn sponsorship revenue
              </button>
              .
            </p>

            <a
              id="newsletter-hero-trial"
              onClick={() => trackNewsletterAction('hero_try_trial')}
              href="#pricing"
              className="mt-6 -ml-0.5 flex min-h-12 w-fit items-center justify-center rounded-full bg-nl-brand px-7 font-semibold text-nl-ink transition hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-nl-ink sm:mt-8"
            >
              Start free trial
            </a>
          </section>

          <div className="newsletter-home-hero-demo relative z-40 min-w-0 xl-wide:self-end">
            <NewsletterHeroDemo />
          </div>
        </div>

        <div className="mt-7 xl-wide:mt-0">{socialProof ?? <CustomerLogos />}</div>
      </div>
      {benefit && <NewsletterBenefitDialog benefit={benefit} onClose={() => setBenefit(null)} />}
    </div>
  );
}
